<script setup>
import { computed, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { pushToDataLayerDirectly } from '@/ui/scripts/analytics/analytics.methods';

const { listView } = inject('config');
const store = useStore();
const projectsAToZ = ref(null);
const projectsLocation = ref(null);
const isLoading = computed(() => store.state.loader.isLoading);
const projectsFilterModel = computed({
  get() {
    return store.state.projectsMap.projectsFilter;
  },
  set(value) {
    store.commit(`projectsMap/SET_PROJECTS_LIST_FILTER`, value);
  },
});

async function filterChangeHandler(event) {
  if (isLoading.value === false) {
    if (event.target.value === 'projectsAToZ') {
      await store.dispatch('projectsMap/fetchProjectsByAToZ');
      projectsAToZ.value.focus();
      pushToDataLayerDirectly({
        event: 'project_list_category_toggle',
        link_text: listView.filterAToZLabel,
      });
    } else {
      await store.dispatch('projectsMap/fetchProjectsByLocation');
      projectsLocation.value.focus();
      pushToDataLayerDirectly({
        event: 'project_list_category_toggle',
        link_text: listView.filterLocationLabel,
      });
    }
  }
}
</script>

<template>
  <fieldset class="c-projects-filter" aria-controls="projectsList">
    <legend class="visually-hidden">View by location or A-Z</legend>
    <div class="c-projects-filter__inner">
      <input
        v-model="projectsFilterModel"
        @change="filterChangeHandler"
        :disabled="isLoading"
        class="c-projects-filter__input visually-hidden"
        name="projectsFilter"
        id="projectsLocation"
        ref="projectsLocation"
        type="radio"
        :value="'projectsLocation'"
      />
      <label class="c-projects-filter__label" for="projectsLocation">
        {{ listView.filterLocationLabel }}
      </label>
      <input
        v-model="projectsFilterModel"
        @change="filterChangeHandler"
        :disabled="isLoading"
        class="c-projects-filter__input visually-hidden"
        name="projectsFilter"
        id="projectsAToZ"
        ref="projectsAToZ"
        type="radio"
        :value="'projectsAToZ'"
      />
      <label class="c-projects-filter__label" for="projectsAToZ">
        {{ listView.filterAToZLabel }}
      </label>
    </div>
  </fieldset>
</template>

<style lang="scss" scoped>
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/focus';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/units';

.c-projects-filter {
  background-color: map-get($color-grey, 6);
  border-radius: $border-radius-large;
  padding: $spacing-small;
}

.c-projects-filter__inner {
  display: flex;
  flex-direction: column;
  gap: $spacing-x-small;

  @include mq($from: medium) {
    gap: $spacing-medium;
    flex-direction: row;
  }
}

.c-projects-filter__label {
  @include largeprint-style();

  margin: 0;
  flex: 1 0 auto;
  justify-content: center;
  padding: $spacing-small $spacing-large;
  border-radius: $border-radius-large;
  cursor: pointer;
  color: $color-brand-two;

  &:hover {
    text-decoration: underline;
  }

  input:checked + & {
    background-color: $color-brand-two;
    color: $white;
  }

  input:focus-visible + & {
    @include focus-outline;
    outline-offset: $spacing-x-small;
  }
}
</style>
